import type { ComponentChildren } from 'preact'
import { useId } from 'preact/hooks'
import styles from './Field.module.css'

interface FieldShellProps {
  label: string
  hint?: string
  error?: string
  children: (ids: { inputId: string; describedBy: string | undefined }) => ComponentChildren
}

/**
 * Label, hint, and error wrapper.
 *
 * Errors are associated through `aria-describedby` and marked `role="alert"`,
 * and every error names its field and the correction — an explicit
 * accessibility criterion in docs/ui-ux-spec.md. Error state is never signalled
 * by colour alone: there is always text.
 */
export function FieldShell({ label, hint, error, children }: FieldShellProps) {
  const inputId = useId()
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`

  const describedBy = [
    hint === undefined ? undefined : hintId,
    error === undefined ? undefined : errorId,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div class={styles.field}>
      <label class={styles.label} for={inputId}>
        {label}
      </label>
      {hint === undefined ? null : (
        <p class={styles.hint} id={hintId}>
          {hint}
        </p>
      )}
      {children({ inputId, describedBy: describedBy === '' ? undefined : describedBy })}
      {error === undefined ? null : (
        <p class={styles.error} id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

interface NumericFieldProps {
  label: string
  value: string
  onInput: (value: string) => void
  hint?: string
  error?: string
  suffix?: string
  placeholder?: string
  autoFocus?: boolean
}

/**
 * Numeric entry.
 *
 * `type="text"` with `inputMode="decimal"` rather than `type="number"`:
 * number inputs on iOS bring up a keypad without a decimal separator in some
 * locales, and silently discard values the browser considers invalid. Keeping
 * the raw string means what the user typed is what we validate.
 *
 * Shorthand interpretation (`684` meaning 68.4 kg) deliberately does NOT ship
 * (D-035).
 */
export function NumericField({
  label,
  value,
  onInput,
  hint,
  error,
  suffix,
  placeholder,
  autoFocus = false,
}: NumericFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ inputId, describedBy }) => (
        <div class={styles.inputRow}>
          <input
            id={inputId}
            class={styles.input}
            type="text"
            inputMode="decimal"
            enterKeyHint="done"
            autocomplete="off"
            value={value}
            placeholder={placeholder ?? ''}
            aria-describedby={describedBy}
            aria-invalid={error === undefined ? undefined : 'true'}
            /* Opt-in per call site, never a default. Today uses it because
               "the weight field is immediately available" is the screen's whole
               premise; forms with several fields do not. */
            autofocus={autoFocus}
            onInput={(event) => onInput((event.currentTarget as HTMLInputElement).value)}
          />
          {suffix === undefined ? null : <span class={styles.suffix}>{suffix}</span>}
        </div>
      )}
    </FieldShell>
  )
}

interface DateFieldProps {
  label: string
  value: string
  onInput: (value: string) => void
  hint?: string
  error?: string
  min?: string
  max?: string
}

/** Native date input, which gives iOS its own well-understood date picker. */
export function DateField({ label, value, onInput, hint, error, min, max }: DateFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ inputId, describedBy }) => (
        <div class={styles.inputRow}>
          <input
            id={inputId}
            class={styles.input}
            type="date"
            value={value}
            min={min ?? ''}
            max={max ?? ''}
            aria-describedby={describedBy}
            aria-invalid={error === undefined ? undefined : 'true'}
            onInput={(event) => onInput((event.currentTarget as HTMLInputElement).value)}
          />
        </div>
      )}
    </FieldShell>
  )
}

interface TextFieldProps {
  label: string
  value: string
  onInput: (value: string) => void
  hint?: string
  error?: string
  placeholder?: string
  /** Renders a textarea, for the fields that hold a sentence rather than a word. */
  multiline?: boolean
}

/** Free text. Optional profile and note fields only — never a measured value. */
export function TextField({
  label,
  value,
  onInput,
  hint,
  error,
  placeholder,
  multiline = false,
}: TextFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ inputId, describedBy }) => (
        <div class={styles.inputRow}>
          {multiline ? (
            <textarea
              id={inputId}
              class={styles.input}
              rows={3}
              value={value}
              placeholder={placeholder ?? ''}
              aria-describedby={describedBy}
              aria-invalid={error === undefined ? undefined : 'true'}
              onInput={(event) => onInput((event.currentTarget as HTMLTextAreaElement).value)}
            />
          ) : (
            <input
              id={inputId}
              class={styles.input}
              type="text"
              value={value}
              placeholder={placeholder ?? ''}
              aria-describedby={describedBy}
              aria-invalid={error === undefined ? undefined : 'true'}
              onInput={(event) => onInput((event.currentTarget as HTMLInputElement).value)}
            />
          )}
        </div>
      )}
    </FieldShell>
  )
}

interface SelectFieldProps<T extends string> {
  label: string
  value: T
  options: readonly { readonly value: T; readonly label: string }[]
  onChange: (value: T) => void
  hint?: string
  error?: string
}

/**
 * Native select.
 *
 * Used where a list of radio rows would be longer than the choice deserves —
 * seven weekday names, for instance. iOS renders it as its own wheel picker, so
 * it stays one-handed without any custom control.
 */
export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
  error,
}: SelectFieldProps<T>) {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ inputId, describedBy }) => (
        <div class={styles.inputRow}>
          <select
            id={inputId}
            class={styles.input}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={error === undefined ? undefined : 'true'}
            onChange={(event) => onChange((event.currentTarget as HTMLSelectElement).value as T)}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </FieldShell>
  )
}

interface RadioGroupProps<T extends string> {
  legend: string
  hint?: string
  error?: string
  value: T | undefined
  options: readonly { readonly value: T; readonly label: string; readonly description?: string }[]
  onChange: (value: T) => void
}

/**
 * Single-select list.
 *
 * A list of large rows rather than a segmented control: five phase names do not
 * fit legibly across an iPhone, and each row clears the 44 px target
 * comfortably.
 */
export function RadioGroup<T extends string>({
  legend,
  hint,
  error,
  value,
  options,
  onChange,
}: RadioGroupProps<T>) {
  const groupName = useId()

  return (
    <fieldset class={styles.fieldset}>
      <legend class={styles.label}>{legend}</legend>
      {hint === undefined ? null : <p class={styles.hint}>{hint}</p>}
      <div class={styles.options}>
        {options.map((option) => (
          <label key={option.value} class={styles.option}>
            <input
              type="radio"
              name={groupName}
              class={styles.radio}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span class={styles.optionText}>
              <span class={styles.optionLabel}>{option.label}</span>
              {option.description === undefined ? null : (
                <span class={styles.optionDescription}>{option.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {error === undefined ? null : (
        <p class={styles.error} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  )
}
